import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const healthWellness: TemplateRegistration = {
  meta: {
    id: 'health-wellness',
    name: 'Health & Wellness',
    category: 'Creative',
    description: 'Fresh, calming wellness design with organic shapes and a soothing green palette',
    previewImage: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Comfortaa', 'Nunito']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &middot; ${reviewCount} reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section">
        <div class="container">
          <div class="about-layout">
            <span class="section-label">Our Story</span>
            <h2 class="section-title">About Us</h2>
            <p class="about-text">${safeAbout}</p>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card">
            <div class="service-icon">${getIconSvg(s.icon || 'heart')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-mint">
        <div class="container">
          <div class="section-header">
            <span class="section-label">What We Offer</span>
            <h2 class="section-title">Our Services</h2>
          </div>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Gallery</span>
            <h2 class="section-title">See Our Space</h2>
          </div>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-accent"></div>
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
              <div class="testimonial-author">
                <div class="testimonial-avatar">${tName.charAt(0)}</div>
                <div>
                  <strong>${tName}</strong>
                  ${tRole ? `<span>${tRole}</span>` : ''}
                </div>
              </div>
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-mint">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Testimonials</span>
            <h2 class="section-title">What People Say</h2>
          </div>
          <div class="testimonials-stack">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <details class="faq-item">
            <summary class="faq-q">${q}</summary>
            <p class="faq-a">${a}</p>
          </details>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container">
          <div class="section-header">
            <span class="section-label">FAQ</span>
            <h2 class="section-title">Common Questions</h2>
          </div>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-mint">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Pricing</span>
            <h2 class="section-title">Our Rates</h2>
          </div>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Visit Us</span>
            <h2 class="section-title">Our Hours</h2>
          </div>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-mint">
        <div class="container">
          <div class="section-header">
            <span class="section-label">Reach Out</span>
            <h2 class="section-title">Contact Us</h2>
          </div>
          <div class="contact-layout">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<div><span class="contact-label">Phone</span><a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<div><span class="contact-label">Location</span><span>${sanitizeText(address)}</span></div></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">Open in Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Email Address" required>
              <textarea placeholder="How can we help?" rows="5" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #f0fdf4; --bg-mint: #ecfdf5; --white: #ffffff;
      --green: #064e3b; --emerald: #10b981; --teal: #14b8a6;
      --text: #064e3b; --text-muted: #4b7c6f;
      --border: #d1fae5; --font-heading: 'Comfortaa', cursive; --font-body: 'Nunito', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.7; }
    .container { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--emerald); text-decoration: none; transition: color .2s; }
    a:hover { color: var(--teal); }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(240,253,244,.92); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 1100px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-weight: 700; font-size: 1.2rem; color: var(--green); }
    .nav-links { display: flex; gap: 28px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .875rem; font-weight: 600; transition: color .2s; }
    .nav-links a:hover { color: var(--emerald); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 22px; height: 2px; background: var(--green); margin: 5px 0; border-radius: 2px; transition: .3s; }

    /* HERO */
    .hero { padding: 180px 0 140px; background: var(--bg); text-align: center; position: relative; overflow: hidden; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.4rem, 5vw, 3.6rem); font-weight: 700; line-height: 1.25; color: var(--green); margin-bottom: 20px; max-width: 700px; margin-left: auto; margin-right: auto; }
    .hero-sub { font-size: 1.15rem; color: var(--text-muted); max-width: 520px; margin: 0 auto 40px; line-height: 1.8; }
    .hero-cta { display: inline-block; padding: 16px 48px; background: linear-gradient(135deg, var(--emerald), var(--teal)); color: #fff; font-family: var(--font-heading); font-weight: 600; font-size: 1rem; border-radius: 50px; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(16,185,129,.25); color: #fff; }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-mint { background: var(--bg-mint); }
    .section-header { text-align: center; margin-bottom: 48px; }
    .section-label { display: inline-block; font-family: var(--font-heading); font-size: .7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2.5px; color: var(--emerald); margin-bottom: 8px; }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.5rem, 3vw, 2rem); font-weight: 700; color: var(--green); }

    /* ABOUT */
    .about-layout { max-width: 700px; margin: 0 auto; text-align: center; position: relative; padding: 40px 0; }
    .about-layout::before, .about-layout::after { content: ''; display: block; width: 80px; height: 2px; background: linear-gradient(135deg, var(--emerald), var(--teal)); margin: 0 auto 32px; }
    .about-layout::after { margin: 32px auto 0; }
    .about-text { font-size: 1.05rem; color: var(--text-muted); line-height: 1.9; }

    /* SERVICES */
    .services-grid { display: flex; flex-direction: column; gap: 0; max-width: 800px; margin: 0 auto; }
    .service-card { display: grid; grid-template-columns: auto 1fr; gap: 24px; align-items: center; padding: 32px 24px; background: var(--white); border-radius: 20px; margin-bottom: 16px; box-shadow: 0 2px 12px rgba(6,78,59,.05); transition: transform .2s, box-shadow .2s; border: 1px solid var(--border); }
    .service-card:nth-child(even) { direction: rtl; }
    .service-card:nth-child(even) > * { direction: ltr; }
    .service-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(6,78,59,.1); }
    .service-icon { display: inline-flex; align-items: center; justify-content: center; width: 56px; height: 56px; border-radius: 16px; background: linear-gradient(135deg, #d1fae5, #a7f3d0); color: var(--green); }
    .icon-char { font-size: 24px; line-height: 1; }
    .service-card h3 { font-family: var(--font-heading); font-size: 1rem; font-weight: 700; margin-bottom: 8px; color: var(--green); }
    .service-card p { color: var(--text-muted); font-size: .9rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 12px; color: var(--emerald); font-weight: 700; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; border-radius: 16px; transition: transform .3s; }
    .gallery-item:hover img { transform: scale(1.03); }

    /* TESTIMONIALS */
    .testimonials-stack { max-width: 800px; margin: 0 auto; }
    .testimonial-card { background: var(--white); border-radius: 16px; overflow: hidden; box-shadow: 0 2px 12px rgba(6,78,59,.05); margin-bottom: 20px; }
    .testimonial-card:first-child { padding: 0; }
    .testimonial-card:first-child .testimonial-body { padding: 48px 40px; }
    .testimonial-card:first-child .testimonial-body p { font-size: 1.3rem; line-height: 1.8; }
    .testimonial-accent { width: 5px; flex-shrink: 0; background: linear-gradient(180deg, var(--emerald), var(--teal)); border-radius: 5px 0 0 5px; display: none; }
    .testimonial-card:not(:first-child) { display: flex; gap: 0; }
    .testimonial-card:not(:first-child) .testimonial-accent { display: block; }
    .testimonial-body { padding: 28px; flex: 1; }
    .testimonial-body p { font-size: .95rem; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; font-style: italic; }
    .testimonial-stars { margin-bottom: 12px; }
    .star { color: var(--border); font-size: 1rem; }
    .star.filled { color: #f59e0b; }
    .testimonial-author { display: flex; align-items: center; gap: 12px; }
    .testimonial-avatar { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, var(--emerald), var(--teal)); color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: .85rem; font-family: var(--font-heading); }
    .testimonial-author strong { display: block; font-size: .9rem; color: var(--green); }
    .testimonial-author span { color: var(--text-muted); font-size: .8rem; }

    /* FAQ */
    .faq-list { max-width: 680px; margin: 0 auto; }
    .faq-item { background: var(--white); border-radius: 16px; margin-bottom: 12px; border: 1px solid var(--border); overflow: hidden; }
    .faq-q { font-family: var(--font-heading); font-size: .95rem; font-weight: 700; color: var(--green); padding: 20px 24px; cursor: pointer; list-style: none; display: flex; align-items: center; justify-content: space-between; }
    .faq-q::-webkit-details-marker { display: none; }
    .faq-q::after { content: '+'; font-size: 1.3rem; color: var(--emerald); font-weight: 300; }
    details[open] .faq-q::after { content: '-'; }
    .faq-a { color: var(--text-muted); font-size: .9rem; line-height: 1.7; padding: 0 24px 20px; }

    /* PRICE LIST */
    .price-list { max-width: 680px; margin: 0 auto; }
    .price-row { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; background: var(--white); border-radius: 16px; margin-bottom: 12px; border: 1px solid var(--border); }
    .price-info h3 { font-family: var(--font-heading); font-size: .9rem; font-weight: 700; color: var(--green); margin-bottom: 2px; }
    .price-info p { color: var(--text-muted); font-size: .85rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 700; color: var(--emerald); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 420px; margin: 0 auto; }
    .hours-list li { padding: 14px 20px; border-bottom: 1px solid var(--border); color: var(--text-muted); font-size: .9rem; }
    .hours-list li:last-child { border-bottom: none; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: linear-gradient(135deg, var(--emerald), var(--teal)); padding: 14px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.1rem; }
    .review-badge .star { opacity: .5; }
    .review-badge .star.filled { opacity: 1; }
    .review-text { color: #fff; font-weight: 600; font-size: .9rem; }

    /* PROMO */
    .promo-banner { background: #d1fae5; padding: 14px 0; }
    .promo-inner { display: flex; align-items: center; gap: 14px; justify-content: center; flex-wrap: wrap; }
    .promo-badge { background: var(--emerald); color: #fff; padding: 5px 16px; border-radius: 50px; font-size: .8rem; font-weight: 700; }
    .promo-content strong { color: var(--green); font-size: .9rem; }
    .promo-content span { color: var(--text-muted); font-size: .85rem; margin-left: 6px; }

    /* CONTACT */
    .contact-layout { max-width: 600px; margin: 0 auto; }
    .contact-info { display: flex; flex-direction: column; gap: 24px; margin-bottom: 32px; }
    .contact-item { display: flex; align-items: flex-start; gap: 14px; }
    .contact-item .icon-char { color: var(--emerald); flex-shrink: 0; font-size: 20px; line-height: 1; margin-top: 2px; }
    .contact-label { display: block; font-size: .7rem; text-transform: uppercase; letter-spacing: 1px; color: var(--text-muted); font-weight: 700; margin-bottom: 2px; }
    .contact-item a { color: var(--green); font-weight: 600; }
    .map-link { display: inline-block; margin-top: 4px; font-weight: 600; font-size: .85rem; }
    .contact-form { display: flex; flex-direction: column; gap: 14px; }
    .contact-form input, .contact-form textarea { background: var(--white); border: 1px solid var(--border); color: var(--text); padding: 14px 18px; border-radius: 14px; font-family: var(--font-body); font-size: .9rem; outline: none; transition: border-color .2s, box-shadow .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--emerald); box-shadow: 0 0 0 3px rgba(16,185,129,.12); }
    .contact-form button { padding: 14px; background: linear-gradient(135deg, var(--emerald), var(--teal)); color: #fff; border-radius: 50px; font-family: var(--font-heading); font-weight: 600; font-size: .95rem; border: none; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(16,185,129,.2); }

    /* FOOTER */
    .footer { background: var(--green); padding: 48px 0; text-align: center; }
    .footer-brand { font-family: var(--font-heading); font-weight: 700; font-size: 1.1rem; color: #fff; margin-bottom: 8px; }
    .footer p { color: #a7f3d0; font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(240,253,244,.98); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .service-card:nth-child(even) { direction: ltr; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero { padding: 120px 0 80px; }
      .price-row { flex-direction: column; align-items: flex-start; gap: 8px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="container">
      <h1>${safeHeadline}</h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Book Now</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <div class="footer-brand">${safeName}</div>
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
